import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppConfig } from '@/Config/AppConfigContext'
import { clearConfig } from '@/Config/ConfigService'
import { AppHeader } from '@/components/AppHeader'
import { MfaApprovalOverlay } from '@/components/MfaApprovalOverlay'
import { PublicHeader } from '@/components/PublicHeader'
import { PublicTabNavigation } from '@/components/PublicTabNavigation'
import { AdminScreen } from '@/components/screens/AdminScreen'
import { AskReinzScreen } from '@/components/screens/AskReinzScreen'
import { ComplianceScreen } from '@/components/screens/ComplianceScreen'
import { EducationScreen } from '@/components/screens/EducationScreen'
import { EventsScreen } from '@/components/screens/EventsScreen'
import { HomeScreen } from '@/components/screens/HomeScreen'
import { LandingScreen } from '@/components/screens/LandingScreen'
import { LoginScreen } from '@/components/screens/LoginScreen'
import { MarketInsightsScreen } from '@/components/screens/MarketInsightsScreen'
import { MemberBenefitsScreen } from '@/components/screens/MemberBenefitsScreen'
import { MyPracticeScreen } from '@/components/screens/MyPracticeScreen'
import { type PropertyDetails, PropertyToolsScreen } from '@/components/screens/PropertyToolsScreen'
import { PublicHomeScreen } from '@/components/screens/PublicHomeScreen'
import { AgentSearchScreen } from '@/components/screens/public/AgentSearchScreen'
import { GuidesScreen } from '@/components/screens/public/GuidesScreen'
import { MarketSnapshotScreen } from '@/components/screens/public/MarketSnapshotScreen'
import { RoleSelectionScreen } from '@/components/screens/RoleSelectionScreen'
import { TabNavigation } from '@/components/TabNavigation'
import { setUser, trackPageView } from '@/lib/analytics'
import { getDeviceId } from '@/lib/deviceId'
import { setActiveSessionId } from '@/lib/sessionTracker'
import { telemetry } from '@/lib/telemetry'

type UserRole = 'none' | 'agent' | 'public'

function detectPlatform(): string {
  if (window.matchMedia('(display-mode: standalone)').matches) return 'pwa'
  if (/iphone|ipad|ipod/i.test(navigator.userAgent)) return 'ios'
  if (/android/i.test(navigator.userAgent)) return 'android'
  return 'web'
}

const tabTitles: Record<string, string> = {
  home: '',
  ask: 'AskREINZ',
  tools: 'Property Estimate',
  insights: 'Market Insights',
  compliance: 'Compliance',
  practice: 'My Profile',
  benefits: 'Offers',
  events: 'Events',
  education: 'Education',
}

const publicTabTitles: Record<string, string> = {
  home: '',
  agents: 'Find an Agent',
  market: 'Market Data',
  guides: 'Guides',
}

const AGENT_TABS = new Set([
  'home',
  'ask',
  'tools',
  'insights',
  'compliance',
  'practice',
  'benefits',
  'events',
  'education',
  'admin',
])

const Index = () => {
  const navigate = useNavigate()
  const { tab: urlTab } = useParams<{ tab?: string }>()

  const [userRole, setUserRole] = useState<UserRole>('none')
  const [showLanding, setShowLanding] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [username, setUsername] = useState('')
  const [publicTab, setPublicTab] = useState('home')
  const [initialSubTab, setInitialSubTab] = useState<string | undefined>(undefined)

  // For agents: active tab is always derived from the URL so refresh restores the screen.
  // For public users: tab is local state (no URL routing needed).
  const activeTab = isLoggedIn ? (urlTab && AGENT_TABS.has(urlTab) ? urlTab : 'home') : publicTab

  const setActiveTab = (tab: string) => {
    if (isLoggedIn) {
      navigate(tab === 'home' ? '/' : `/${tab}`)
    } else {
      setPublicTab(tab)
    }
  }
  const [chatMessages, setChatMessages] = useState<
    { id: string; role: 'user' | 'assistant'; content: string; timestamp: Date }[]
  >([])
  const [chatImisConversationId, setChatImisConversationId] = useState<string | null>(null)
  const [chatRagConversationId, setChatRagConversationId] = useState<string | null>(null)
  const [estValAddress, setEstValAddress] = useState('')
  const [estSelectedRafid, setEstSelectedRafid] = useState<number | null>(null)
  const [estValSuburb, setEstValSuburb] = useState('')
  const [estValBedrooms, setEstValBedrooms] = useState('')
  const [estValBathrooms, setEstValBathrooms] = useState('')
  const [estValFloorArea, setEstValFloorArea] = useState('')
  const [estValLandArea, setEstValLandArea] = useState('')
  const [estValGarages, setEstValGarages] = useState('')
  const [estValResult, setEstValResult] = useState<{
    low: number
    high: number
    rating: number
  } | null>(null)
  const [estPropertyDetails, setEstPropertyDetails] = useState<PropertyDetails | null>(null)
  const { instance, accounts, inProgress } = useMsal()
  const cfg = useAppConfig()

  const partyId = accounts[0]?.localAccountId ?? ''
  const userEmail =
    (accounts[0]?.idTokenClaims as any)?.email ??
    (accounts[0]?.idTokenClaims as any)?.emails?.[0] ??
    accounts[0]?.username ??
    ''

  // Session tracking
  const sessionIdRef = useRef<number | null>(null)
  const markSessionLoggedOut = useCallback(() => {
    if (sessionIdRef.current) {
      // keepalive: the caller immediately navigates away after this (MSAL redirect) —
      // without it, the browser can abort the request before it lands.
      fetch(`/api/user-sessions/${sessionIdRef.current}/logout`, {
        method: 'POST',
        keepalive: true,
      }).catch(() => {})
    }
  }, [])

  // Admin access
  const [isAdmin, setIsAdmin] = useState(false)

  // MFA approval state
  const [mfaChallenge, setMfaChallenge] = useState<{
    challengeId: string
    expiresAt: string
  } | null>(null)
  const mfaPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const handleMfaResolved = useCallback(() => setMfaChallenge(null), [])

  useEffect(() => {
    if (accounts.length > 0) {
      setUserRole('agent')
      setIsLoggedIn(true)
      setUsername(accounts[0].username)
      setUser(accounts[0].localAccountId)
      telemetry.setUser(accounts[0].localAccountId)

      // Create session on login
      const userId = accounts[0].localAccountId
      const email =
        (accounts[0].idTokenClaims as any)?.email ??
        (accounts[0].idTokenClaims as any)?.emails?.[0] ??
        accounts[0].username ??
        ''
      const displayName = accounts[0].name ?? ''
      fetch('/api/user-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email,
          displayName,
          platform: detectPlatform(),
          lastTab: 'home',
          deviceId: getDeviceId(),
        }),
      })
        .then((r) => r.json())
        .then((d) => {
          sessionIdRef.current = d.sessionId
          setActiveSessionId(d.sessionId)
        })
        .catch(() => {})
    }
  }, [accounts])

  // Check admin status once email is resolved
  useEffect(() => {
    if (!isLoggedIn || !userEmail) return
    fetch(`/api/admin/isadmin?email=${encodeURIComponent(userEmail)}`)
      .then((r) => r.json())
      .then((d) => setIsAdmin(d.isAdmin ?? false))
      .catch(() => {})
  }, [isLoggedIn, userEmail])

  const handleAdminAccess = () => {
    if (isAdmin) setActiveTab('admin')
  }

  // MFA polling: check every 3s for a pending approval challenge for this user
  useEffect(() => {
    if (!isLoggedIn || !userEmail) return
    mfaPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/mfa/pending?email=${encodeURIComponent(userEmail)}`)
        const data = await res.json()
        if (data.pending && data.challengeId) {
          // Only update if it's a different challenge — avoids re-render flicker
          setMfaChallenge((prev) =>
            prev?.challengeId === data.challengeId
              ? prev
              : { challengeId: data.challengeId, expiresAt: data.expiresAt },
          )
        } else if (!data.pending) {
          setMfaChallenge(null)
        }
      } catch {
        /* silent — polling failure should not disrupt the app */
      }
    }, 3000)
    return () => {
      if (mfaPollRef.current) clearInterval(mfaPollRef.current)
    }
  }, [isLoggedIn, userEmail])

  // const handleSelectAgent = () => {
  //   setUserRole("agent");
  // };

  const handleSelectAgent = () => {
    instance.loginRedirect({ scopes: cfg.apiScopes })
  }

  const handleSelectPublic = () => {
    setUserRole('public')
    setPublicTab('home')
  }

  const handleLogin = (name: string) => {
    setUsername(name)
    setIsLoggedIn(true)
  }

  useEffect(() => {
    const allTitles = { ...tabTitles, ...publicTabTitles }
    const name = allTitles[activeTab] || activeTab
    trackPageView(name || 'Home')

    // Update LastTab + LastActivityAt on tab change
    if (sessionIdRef.current) {
      fetch(`/api/user-sessions/${sessionIdRef.current}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lastTab: activeTab }),
      }).catch(() => {})
    }
  }, [activeTab, accounts])

  const handleNavigate = (tab: string, subTab?: string) => {
    setInitialSubTab(subTab)
    setActiveTab(tab)
  }

  const handleBackToRoleSelection = () => {
    clearConfig()
    setUserRole('none')
    setIsLoggedIn(false)
    navigate('/')
  }

  // While MSAL is processing a redirect, or accounts exist but state hasn’t caught up yet, avoid flashing the role selection screen
  if (inProgress !== InteractionStatus.None || (accounts.length > 0 && userRole === 'none')) {
    return <div className="p-6 text-center text-muted-foreground">Loading…</div>
  }

  // Show role selection if no role chosen
  if (userRole === 'none') {
    if (showLanding) {
      return <LandingScreen onBackToLogin={() => setShowLanding(false)} />
    }
    return (
      <RoleSelectionScreen
        onSelectAgent={handleSelectAgent}
        onSelectPublic={handleSelectPublic}
        onLearnMore={() => setShowLanding(true)}
      />
    )
  }

  // Public experience
  if (userRole === 'public') {
    const renderPublicScreen = () => {
      switch (activeTab) {
        case 'home':
          return <PublicHomeScreen onNavigate={handleNavigate} />
        case 'agents':
          return <AgentSearchScreen onBack={() => setActiveTab('home')} />
        case 'market':
          return <MarketSnapshotScreen onBack={() => setActiveTab('home')} />
        case 'guides':
          return <GuidesScreen onBack={() => setActiveTab('home')} />
        default:
          return <PublicHomeScreen onNavigate={handleNavigate} />
      }
    }

    return (
      <div className="h-dvh bg-muted flex justify-center">
        <div className="w-full max-w-lg h-dvh bg-background flex flex-col shadow-xl relative">
          <PublicHeader
            title={publicTabTitles[activeTab]}
            onBackToRoleSelection={handleBackToRoleSelection}
          />

          <main className="flex-1 overflow-auto">
            <div className="px-4 py-4">{renderPublicScreen()}</div>
          </main>

          <PublicTabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
      </div>
    )
  }

  // Agent flow - show login if not logged in
  if (!isLoggedIn) {
    return <LoginScreen onLogin={handleLogin} />
  }

  const renderScreen = () => {
    switch (activeTab) {
      case 'home':
        return (
          <HomeScreen onNavigate={handleNavigate} username={accounts[0]?.name} partyId={partyId} />
        )
      case 'ask':
        return (
          <AskReinzScreen
            messages={chatMessages}
            setMessages={setChatMessages}
            imisConversationId={chatImisConversationId}
            setImisConversationId={setChatImisConversationId}
            ragConversationId={chatRagConversationId}
            setRagConversationId={setChatRagConversationId}
            userName={accounts[0]?.name}
          />
        )
      case 'tools':
        return (
          <PropertyToolsScreen
            initialTab={initialSubTab}
            valAddress={estValAddress}
            setValAddress={setEstValAddress}
            selectedRafid={estSelectedRafid}
            setSelectedRafid={setEstSelectedRafid}
            valSuburb={estValSuburb}
            setValSuburb={setEstValSuburb}
            valBedrooms={estValBedrooms}
            setValBedrooms={setEstValBedrooms}
            valBathrooms={estValBathrooms}
            setValBathrooms={setEstValBathrooms}
            valFloorArea={estValFloorArea}
            setValFloorArea={setEstValFloorArea}
            valLandArea={estValLandArea}
            setValLandArea={setEstValLandArea}
            valGarages={estValGarages}
            setValGarages={setEstValGarages}
            valResult={estValResult}
            setValResult={setEstValResult}
            propertyDetails={estPropertyDetails}
            setPropertyDetails={setEstPropertyDetails}
          />
        )
      case 'insights':
        return <MarketInsightsScreen />
      case 'compliance':
        return <ComplianceScreen />
      case 'practice':
        return (
          <MyPracticeScreen
            username={username}
            partyId={accounts[0]?.localAccountId ?? ''}
            onNavigate={handleNavigate}
            onSignOut={markSessionLoggedOut}
          />
        )
      case 'events':
        return (
          <EventsScreen
            onBack={() => setActiveTab('home')}
            partyId={partyId}
            initialTab={initialSubTab}
          />
        )
      case 'benefits':
        return <MemberBenefitsScreen partyId={partyId} />
      case 'education':
        return (
          <EducationScreen
            onBack={() => setActiveTab('home')}
            partyId={partyId}
            initialTab={initialSubTab}
          />
        )
      case 'admin':
        return <AdminScreen onBack={() => setActiveTab('home')} userEmail={userEmail} />
      default:
        return <HomeScreen onNavigate={handleNavigate} username={username} partyId={partyId} />
    }
  }

  return (
    <div className="h-dvh bg-muted flex justify-center">
      <div className="w-full max-w-lg h-dvh bg-background flex flex-col shadow-xl relative">
        <AppHeader
          title={tabTitles[activeTab]}
          userName={accounts[0]?.name}
          onAdminAccess={handleAdminAccess}
          isAdmin={isAdmin}
          onLogout={markSessionLoggedOut}
        />

        <main className="flex-1 overflow-auto">
          {activeTab === 'events' || activeTab === 'education' || activeTab === 'admin' ? (
            renderScreen()
          ) : (
            <div className="px-4 py-4">{renderScreen()}</div>
          )}
        </main>

        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {mfaChallenge && (
          <MfaApprovalOverlay
            key={mfaChallenge.challengeId}
            challengeId={mfaChallenge.challengeId}
            expiresAt={mfaChallenge.expiresAt}
            onResolved={handleMfaResolved}
          />
        )}
      </div>
    </div>
  )
}

export default Index
